import { IconName } from '@bytel/trilogy-react';

import type { MobilePlanType } from '@app-types/plan';

export interface OfferCardNugget {
    key: string;
    content: string;
    iconName?: IconName;
    format: 'cms' | 'markdown';
}

function boldFirstWords(text: string, count: number): string {
    const words = text.split(' ');
    return `<b>${words.slice(0, count).join(' ')}</b> ${words.slice(count).join(' ')}`;
}

function boldTargetedWords(inputText: string, targetWords: string[]): string {
    const pattern = targetWords.map((word) => word.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)).join('|');
    return inputText.replaceAll(new RegExp(`(${pattern})`, 'gi'), '<b>$1</b>');
}

export function getOfferCardNuggets(offer: MobilePlanType): OfferCardNugget[] {
    const splittedFromAbroad = offer.fromAbroad?.trim().split('\n');
    const dataFranceEurope = splittedFromAbroad?.[0] ? boldFirstWords(splittedFromAbroad[0], 2) : '';
    const internationalData = splittedFromAbroad?.[1]?.includes('autres destinations')
        ? boldFirstWords(splittedFromAbroad[1], 2)
        : '';
    let callsSms = '';
    if (offer.unlimitedCalls?.trim()) {
        callsSms = boldFirstWords(
            boldTargetedWords(offer.unlimitedCalls, ["2h d'appels et SMS illimités", 'Appels, SMS, MMS illimités']),
            4,
        );
    }
    const internationalCalls = offer.internationalCall?.trim() ? boldFirstWords(offer.internationalCall, 5) : '';
    const bTv = offer.wordingBtv?.trim() ? boldFirstWords(offer.wordingBtv, 5) : '';
    const twinSim = offer.hasDoubleSim ? boldFirstWords('Multi-SIM incluse utilisable sur un second appareil', 2) : '';

    const offerNuggets: OfferCardNugget[] = [
        { key: 'data-france-europe', content: dataFranceEurope, iconName: IconName.AT, format: 'cms' as const },
        {
            key: 'data-international',
            content: internationalData,
            iconName: IconName.AIR_TRAVEL,
            format: 'cms' as const,
        },
        { key: 'calls-sms', content: callsSms, iconName: IconName.MOBILE_GLOBE, format: 'cms' as const },
        {
            key: 'international-calls',
            content: internationalCalls,
            iconName: IconName.PHONE_GLOBE,
            format: 'cms' as const,
        },
        { key: 'btv', content: bTv, iconName: IconName.BTV, format: 'cms' as const },
        { key: 'multi-sim', content: twinSim, iconName: IconName.SIM_CARDS, format: 'cms' as const },
    ].filter(({ content }) => content);

    return [
        ...offerNuggets,
        ...(offer.cmsContent?.addedPepites ?? []).map((pepite, index) => ({
            key: `cms-${pepite.icon ?? 'pepite'}-${index}`,
            content: pepite.pepite ?? '',
            iconName: pepite.icon,
            format: 'markdown' as const,
        })),
    ];
}
